import { memo, Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";

import MegaMenuItemPresenter from "presenters/MegaMenuItemPresenter";

import LinkList from "./components/LinkList";
import { ListsContainer, StyledDivider } from "./styles";

const LinkListContainer = memo(({ links, mainMenuLinks, onLinkActiveChange }) => {
  const [activeLists, setActiveLists] = useState(new Set());

  const LIST_SIZE = 8;

  const lists = useMemo(() => {
    if (links.length === 0) return [];

    const newList = [];
    for (let i = 0; i < links.length; i += LIST_SIZE) {
      newList.push(links.slice(i, i + LIST_SIZE));
    }
    return newList;
  }, [links, LIST_SIZE]);

  const handleListActiveChange = useCallback((listIndex, isActive) => {
    setActiveLists((prev) => {
      const newSet = new Set(prev);
      isActive ? newSet.add(listIndex) : newSet.delete(listIndex);
      return newSet;
    });
  }, []);

  const hasAnyActiveLinks = useMemo(() => activeLists.size > 0, [activeLists.size]);

  useEffect(() => {
    if (onLinkActiveChange) onLinkActiveChange(hasAnyActiveLinks);
  }, [hasAnyActiveLinks, onLinkActiveChange]);

  const listItems = useMemo(
    () =>
      lists.map((list, index) => (
        <Fragment key={index}>
          <LinkList
            list={list}
            mainMenuLinks={mainMenuLinks}
            onLinkActiveChange={(isActive) => handleListActiveChange(index, isActive)}
          />
          {index !== lists.length - 1 && <StyledDivider orientation="vertical" />}
        </Fragment>
      )),
    [lists, mainMenuLinks, handleListActiveChange],
  );

  return (
    <Grid container>
      <Grid>
        <ListsContainer>{listItems}</ListsContainer>
      </Grid>
    </Grid>
  );
});

LinkListContainer.displayName = "LinkListContainer";

LinkListContainer.propTypes = {
  links: PropTypes.arrayOf(MegaMenuItemPresenter.shape()),
  mainMenuLinks: PropTypes.arrayOf(PropTypes.string),
  onLinkActiveChange: PropTypes.func,
};

export default LinkListContainer;
